import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { useAgentInteractions } from '../hooks/useAgentInteractions';
import { 
  APP_VERSION, 
  PLATFORM_PATCH_NOTES,
  getLatestVersion,
  ENABLE_VERSION_TRACKING,
} from '../config/patchNotesData';
import './PatchNotesModal.css';

interface PatchNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentVersion?: string;
}

const PatchNotesModal: React.FC<PatchNotesModalProps> = ({ isOpen, onClose, currentVersion = APP_VERSION }) => {
  const { getLastSeenVersion, recordInteraction } = useAgentInteractions();
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set(['0']));
  const [hasViewedPlatform, setHasViewedPlatform] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Detect dark mode - check multiple sources
  useEffect(() => {
    const checkDarkMode = () => {
      const root = document.documentElement;
      const hasDarkTheme = root.getAttribute('data-theme') === 'dark';
      const hasDarkClass = root.classList.contains('dark') || 
                          document.body.classList.contains('dark') ||
                          document.querySelector('.dark') !== null;
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(hasDarkTheme || hasDarkClass || prefersDark);
    };
    
    checkDarkMode();
    
    // Listen for changes to both class and data-theme attribute
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class', 'data-theme'] 
    });
    
    return () => observer.disconnect();
  }, [isOpen]);
  
  // Check if platform has unseen updates (only when tracking enabled)
  const platformLatestVersion = getLatestVersion('platform');
  const platformLastSeen = getLastSeenVersion('platform');
  const platformHasUpdate = ENABLE_VERSION_TRACKING && !hasViewedPlatform && (platformLastSeen ? platformLastSeen !== platformLatestVersion : true);
  
  // Reset state when modal opens and mark as viewed (only when tracking enabled)
  useEffect(() => {
    if (isOpen) {
      setExpandedNotes(new Set(['0']));
      // Mark platform as viewed when modal opens (only if tracking enabled)
      if (ENABLE_VERSION_TRACKING) {
        recordInteraction('platform', platformLatestVersion, 'patch_notes_view');
        setHasViewedPlatform(true);
      }
    }
  }, [isOpen, platformLatestVersion, recordInteraction]);
  
  const toggleNoteExpanded = useCallback((noteIndex: string) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteIndex)) {
        newSet.delete(noteIndex);
      } else {
        newSet.add(noteIndex);
      }
      return newSet;
    });
  }, []);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  // Dark mode styles
  const darkStyles = {
    modal: isDarkMode ? { background: '#2b2e32', border: '1px solid #3d4045' } : undefined,
    header: isDarkMode ? { borderBottomColor: '#3d4045' } : undefined,
    title: { color: isDarkMode ? '#f8f9fa' : '#1f2124' },
    subtitle: { color: isDarkMode ? '#9da2a8' : '#6f747b' },
    closeButton: { color: isDarkMode ? '#9da2a8' : '#6f747b' },
    noteItem: isDarkMode ? { borderColor: '#3d4045' } : undefined,
    noteHeader: isDarkMode ? { background: '#1f2124', color: '#cfd2d6' } : undefined,
    noteDate: { color: isDarkMode ? '#9da2a8' : '#6f747b' },
    noteContent: isDarkMode ? { background: '#2b2e32', borderTopColor: '#3d4045' } : undefined,
    noteText: { color: isDarkMode ? '#cfd2d6' : '#53575d' },
    strong: { color: isDarkMode ? '#f8f9fa' : '#1f2124' },
    heading: { color: isDarkMode ? '#f8f9fa' : '#1f2124', fontWeight: 600, marginBottom: '0.75rem' },
  };

  return createPortal(
    <div className="patch-notes-modal-overlay" onClick={onClose}>
      <div className="patch-notes-modal" onClick={e => e.stopPropagation()} style={darkStyles.modal}>
        {/* Header */}
        <div className="modal-header" style={darkStyles.header}>
          <div className="header-content">
            <div className="header-icon">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h2 className="modal-title" style={darkStyles.title}>Platform Updates</h2>
              <p className="modal-subtitle" style={darkStyles.subtitle}>
                Scout Central <span className="version-badge">{currentVersion}</span>
              </p>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close" style={darkStyles.closeButton}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="modal-content">
          {PLATFORM_PATCH_NOTES.length > 0 ? (
            <div className="notes-list">
              {PLATFORM_PATCH_NOTES.map((note, index) => (
                <div key={index} className="note-item" style={darkStyles.noteItem}>
                  <button
                    className="note-header"
                    onClick={() => toggleNoteExpanded(String(index))}
                    style={darkStyles.noteHeader}
                  >
                    <div className="note-meta">
                      <span className="note-version">v{note.version}</span>
                      <span className="note-date" style={darkStyles.noteDate}>{note.date}</span>
                    </div>
                    {expandedNotes.has(String(index)) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {expandedNotes.has(String(index)) && (
                    <div className="note-content" style={darkStyles.noteContent}>
                      {note.content ? (
                        <div style={darkStyles.noteText}>
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({children}) => <h1 style={darkStyles.heading}>{children}</h1>,
                              h2: ({children}) => <h2 style={darkStyles.heading}>{children}</h2>,
                              h3: ({children}) => <h3 style={darkStyles.heading}>{children}</h3>,
                              strong: ({children}) => <strong style={darkStyles.strong}>{children}</strong>,
                              p: ({children}) => <p style={darkStyles.noteText}>{children}</p>,
                              li: ({children}) => <li style={darkStyles.noteText}>{children}</li>,
                            }}
                          >
                            {note.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <ul className="highlights-list">
                          {note.highlights.map((highlight, hIdx) => (
                            <li key={hIdx} style={darkStyles.noteText}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-notes" style={darkStyles.noteText}>
              <p>No platform updates available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PatchNotesModal;
